import {
    DeleteOutlined,
    InboxOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import { Upload as Upload_ } from 'antd';
import { useState } from 'react';
import PropTypes from 'prop-types';
import GlobalService from '../../../services/Global.service';
import { useTranslation } from 'react-i18next';
import useNotify from '../../../context/Notification/useNotify';
import ModalDelete from '../../Shared/modal/delete';

const { Dragger } = Upload_;

const allowedType = [
    'image/jpg', // .jepg
    'image/jpeg', // .jepg
    'image/png', // .png
    'application/pdf', // .pdf
];
const allowedTypeDoc = allowedType.filter((type) =>
    type.startsWith('application/')
);
const allowedTypeImage = allowedType.filter((type) =>
    type.startsWith('image/')
);
const imageType = allowedTypeImage.map((type) => type.split('/')[1]);

const typeText = allowedType.map((type) => `.${type.split('/')[1]}`);

export default function UploadCustom({ disabled, onChange }) {
    const { t } = useTranslation();

    const notify = useNotify();

    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openDelete, setOpenDelete] = useState({
        open: false,
        file: null,
    });
    const [fileList, setFileList] = useState([]);

    const beforeUpload = (file) => {
        const fileSize = file.size / 1024 / 1024;
        const fileType = file.type;
        // Check file size
        if (fileSize >= 2) {
            notify({
                type: 'error',
                title: t('common.Fail'),
                description: t('Upload error file size is wrong'),
            });
            return false;
        }
        // Check file type
        if (!allowedType.includes(fileType)) {
            notify({
                type: 'error',
                title: t('common.Fail'),
                description: t('Upload error file type is wrong'),
            });
            return false;
        }
        // Check max file
        if (fileList.length >= 10) {
            notify({
                type: 'error',
                title: t('common.Fail'),
                description: t('Upload error file max count'),
            });
            return false;
        }
        return true;
    };

    const uploadProps = {
        multiple: true,
        showUploadList: false,
        beforeUpload,
        accept: allowedType.join(','),
        fileList,
        maxCount: 10,
        onChange(info) {
            const { status } = info.file;
            if (status === 'uploading') {
                info.file.status = 'done';
            }
        },
        onSuccess(resultData) {
            setFileList([...fileList, resultData]);
            onChange([...fileList, resultData]);
        },
        onError(err) {
            console.log('onError', err);
        },
        onRemove() {
            setFileList([]);
        },
        customRequest: async ({ file, onSuccess, onError }) => {
            let bodyFormDocData = new FormData();
            const fileType = file.type;
            let ImageData = new FormData();
            ImageData.append('image', file);
            ImageData.append('fileName', file.name);
            ImageData.append('action', 'image-create-application');

            if (allowedTypeImage.includes(fileType)) {
                try {
                    const { data } = await GlobalService.uploadPublicImage(
                        ImageData
                    );
                    if (data?.resultData?.imageId) {
                        const item = {
                            fileStorageId: data.resultData?.imageId,
                            fileName: `${data.resultData?.imageName}.${data.resultData?.imageType}`,
                            fileType: data.resultData?.imageType,
                        };
                        onSuccess(item);
                    } else {
                        onError('error');
                    }
                } catch (error) {
                    onError(error);
                    console.log(error);
                }
            } else if (allowedTypeDoc.includes(fileType)) {
                const utf8Name = encodeURIComponent(file.name);
                const header = `form-data; name="uploadFile"; filename*=UTF-8''${utf8Name}`;
                bodyFormDocData.append('doc', file, header);
            }

            const docLength = Array.from(bodyFormDocData.keys()).length;
            if (docLength > 0) {
                bodyFormDocData.append('action', 'form-template');
                try {
                    const { data } = await GlobalService.uploadPublicDoc(
                        bodyFormDocData
                    );
                    if (data?.resultData) {
                        onSuccess(...data.resultData);
                    } else {
                        onError('error');
                    }
                } catch (error) {
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: 'Failed to uploaded.',
                    });
                    onError(error);
                    console.log(error);
                }
            }
        },
    };

    const deleteFile = async (file) => {
        const id = file?.fileStorageId;
        setOpenDelete({
            open: false,
            file: null,
        });
        setDeleteLoading(false);
        const updatedFileList = fileList.filter((f) => f.fileStorageId !== id);
        setFileList(updatedFileList);
        onChange(updatedFileList);
    };

    return (
        <div>
            <Dragger
                disabled={disabled}
                {...uploadProps}
                className="py-4 px-14"
            >
                <p className="">
                    <svg
                        width="42"
                        height="41"
                        viewBox="0 0 42 41"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g clip-path="url(#clip0_2009_14793)">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M21.2449 4.91895C17.9851 4.92774 14.8364 5.93923 12.3677 7.7707C10.4756 9.1732 9.10476 11.0007 8.75155 12.8346C4.61179 13.6208 1.48474 16.7233 1.48474 20.4697C1.48474 24.8217 5.70354 28.2939 10.8239 28.2939H20.0099V17.0463L14.7092 21.6087C14.4773 21.8082 14.1628 21.9203 13.8349 21.9203C13.5069 21.9203 13.1924 21.8082 12.9605 21.6087C12.7286 21.4092 12.5983 21.1386 12.5983 20.8564C12.5983 20.5743 12.7286 20.3037 12.9605 20.1042L20.3705 13.7292C20.4853 13.6302 20.6215 13.5517 20.7716 13.4982C20.9216 13.4446 21.0825 13.417 21.2449 13.417C21.4074 13.417 21.5682 13.4446 21.7183 13.4982C21.8683 13.5517 22.0046 13.6302 22.1193 13.7292L29.5294 20.1042C29.7613 20.3037 29.8916 20.5743 29.8916 20.8564C29.8916 21.1386 29.7613 21.4092 29.5294 21.6087C29.2975 21.8082 28.9829 21.9203 28.655 21.9203C28.327 21.9203 28.0125 21.8082 27.7806 21.6087L22.4799 17.0463V28.2939H32.8244C37.305 28.2939 41.0051 25.2552 41.0051 21.4366C41.0051 17.9601 37.9373 15.1274 34.0051 14.6493C33.4048 9.16682 27.8893 4.91895 21.2449 4.91895ZM20.0099 35.7314V28.2939H22.4799V35.7314C22.4799 36.0132 22.3498 36.2835 22.1182 36.4827C21.8866 36.682 21.5725 36.7939 21.2449 36.7939C20.9174 36.7939 20.6032 36.682 20.3716 36.4827C20.14 36.2835 20.0099 36.0132 20.0099 35.7314Z"
                                fill="#C4C4C4"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_2009_14793">
                                <rect
                                    width="41.0847"
                                    height="40"
                                    fill="white"
                                    transform="translate(0.457642 0.918945)"
                                />
                            </clipPath>
                        </defs>
                    </svg>
                </p>
                <p className="text-[#4F4F4F] font-medium py-2 text-base">
                    Drop file here, paste or{' '}
                    <span className="text-[#3699FF]">browse file</span>{' '}
                </p>
                <p className="text-[#2E2F32] text-xs kanit font-light">
                    สามารถอัปโหลดไฟล์สูงสุด 10 ไฟล์, ขนาดสูงสุด 2 MB. <br />
                    (ประเภทไฟล์ที่อนุญาต : {typeText.join(', ')})
                </p>
            </Dragger>
            <div className="py-2 mt-4">
                {fileList?.map((file) => {
                    return (
                        <div
                            key={file.fileStorageId}
                            className="flex justify-between"
                        >
                            <div
                                type="button"
                                className="text-new space-x-2 w-[94%] flex items-start"
                            >
                                <PaperClipOutlined className="pt-1" />
                                <div
                                    className="overflow-hidden font-semibold"
                                    style={{
                                        wordWrap: 'break-word',
                                    }}
                                >
                                    {file.fileName}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpenDelete({
                                        open: true,
                                        file: file,
                                    });
                                }}
                                className="text-[#95A0A8] cursor-pointer"
                            >
                                <DeleteOutlined />
                            </button>
                        </div>
                    );
                })}
            </div>
            <ModalDelete
                loading={deleteLoading}
                open={openDelete?.open}
                handelClose={() => {
                    setOpenDelete({
                        open: false,
                        file: null,
                    });
                }}
                handelOk={() => {
                    setDeleteLoading(true);
                    const file = openDelete.file;
                    deleteFile(file);
                }}
            />
        </div>
    );
}

// Validated props
UploadCustom.propTypes = {
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
};
